import {
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  Properties,
  simpleHeader,
  withColumnGutters,
} from "@homebound/beam";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { MarkupSummary } from "src/components/MarkupSummary";
import { ChangeEventLineItemType, LotType } from "src/generated/graphqlTypes";
import { CostSourceType } from "src/routes/projects/change-events/ChangeEventLineItemsTable";
import { tradeName } from "src/routes/projects/change-events/guided/utils";
import { ReviewImpactedPrice } from "src/routes/projects/change-events/guided/workflows/components/ReviewImpactedPrice";
import { createProjectBidContractOverviewUrl } from "src/RouteUrls";
import { isDefined } from "src/utils";
import { emptyCellDash } from "src/utils/constants";
import {
  ScopeChangesTable_ChangeEventLineItemFragment,
  ScopeChangesTable_ProjectFragment,
} from "./ScopeChangesTable.generated";

type ScopeChangesTableProps = {
  project: ScopeChangesTable_ProjectFragment;
  lineItems: ScopeChangesTable_ChangeEventLineItemFragment[];
};

/** A change event's line items grouped under their cost code, i.e. the scope the change event adds or removes. */
export function ScopeChangesTable(props: ScopeChangesTableProps) {
  const { project, lineItems } = props;
  const isBoyl = project.lotType.code === LotType.Boyl;
  const columns = useMemo(() => withColumnGutters(createColumns(project.id)), [project.id]);
  const rows = useMemo(() => createRows(lineItems, isBoyl), [lineItems, isBoyl]);
  return (
    <GridTable
      id="scopeChangesTable"
      columns={columns}
      rows={rows}
      fallbackMessage="This change event has no line items yet."
    />
  );
}

type HeaderRow = { kind: "header"; id: string; data: undefined };
type CostCodeRow = { kind: "costCode"; id: string; data: string };
type LineItemRow = {
  kind: "celi";
  id: string;
  data: ScopeChangesTable_ChangeEventLineItemFragment & { hideMarkup: boolean; isPlanBasedScopeOnly: boolean };
};
type Row = HeaderRow | CostCodeRow | LineItemRow;

/**
 * Groups scope changes by cost-code identity, with additions before removals.
 *
 * Each cost code must have one parent row, even when its line items hold separate cost-code objects.
 */
function createRows(lineItems: ScopeChangesTable_ChangeEventLineItemFragment[], isBoyl: boolean): GridDataRow<Row>[] {
  return [
    simpleHeader,
    // Apollo can return separate objects for the same cost code; group by its stable identity.
    ...Object.values(lineItems.groupBy((li) => li.item.costCode.id))
      .sortBy((celis) => celis[0].item.costCode.displayName)
      .map((celis) => ({
        kind: "costCode" as const,
        id: celis[0].item.costCode.id,
        data: celis[0].item.costCode.displayName,
        children: sortCelis(celis).map((celi) => ({
          kind: "celi" as const,
          id: celi.id,
          data: {
            ...celi,
            isPlanBasedScopeOnly: isPlanBasedScopeOnly(celi),
            // BOYL carries the whole price delta on its price-only lines, so every other line would just read -100%
            hideMarkup: isBoyl && !isPriceOnly(celi),
          },
        })),
      })),
  ];
}

function createColumns(projectId: string): GridColumn<Row>[] {
  return [
    collapseColumn<Row>({ celi: emptyCell, w: "32px", sticky: "left" }),
    column<Row>({
      header: "Item",
      costCode: (displayName) => ({ content: displayName, css: Css.smSb.$ }),
      celi: (li) => li.item.displayName,
      w: 2,
    }),
    column<Row>({ header: "Line Item", costCode: emptyCell, celi: (li) => li.name, w: 2 }),
    column<Row>({
      header: "Type",
      costCode: emptyCell,
      celi: (li) => ({
        value: li.typeDetail.code,
        content: () => <span css={typeColor[li.typeDetail.code as ChangeEventLineItemType]}>{li.typeDetail.name}</span>,
      }),
      w: "80px",
    }),
    numericColumn<Row>({
      header: "Qty",
      costCode: emptyCell,
      celi: (li) => {
        if (li.unitOfMeasure?.useQuantity === false) return "N/A";
        if (!isDefined(li.quantity)) return emptyCellDash;
        return li.quantity > 0 ? `+${li.quantity}` : `${li.quantity}`;
      },
    }),
    column<Row>({ header: "UoM", costCode: emptyCell, celi: (li) => li.unitOfMeasure.abbreviation, w: "60px" }),
    numericColumn<Row>({
      header: "Cost",
      costCode: emptyCell,
      celi: (li) =>
        li.isPlanBasedScopeOnly ? emptyCellDash : <ReviewImpactedPrice valueInCents={li.totalCostInCents} />,
    }),
    numericColumn<Row>({
      header: "Markup",
      costCode: emptyCell,
      celi: (li) =>
        li.isPlanBasedScopeOnly || li.hideMarkup ? (
          emptyCellDash
        ) : (
          <MarkupSummary
            budget={li.totalCostInCents}
            price={li.totalPriceInCents}
            displayDirection={li.totalPriceInCents > 0}
            invertColors
            zeroIs="neutral"
          />
        ),
      w: "120px",
    }),
    numericColumn<Row>({
      header: "Price",
      costCode: emptyCell,
      celi: (li) =>
        li.totalPriceInCents === 0 ? emptyCellDash : <ReviewImpactedPrice valueInCents={li.totalPriceInCents} />,
    }),
    column<Row>({
      header: "Cost Source",
      costCode: emptyCell,
      celi: (li) => {
        const bcli = li.proposedBidContractLineItem;
        if (!bcli) return CostSourceType.MANUAL;
        return {
          value: CostSourceType.BID_CONTRACT,
          content: () => (
            <Link to={createProjectBidContractOverviewUrl(projectId, bcli.revision.id)}>
              {CostSourceType.BID_CONTRACT}
            </Link>
          ),
        };
      },
      w: "120px",
    }),
    column<Row>({ header: "Trade", costCode: emptyCell, celi: (li) => tradeName(li), w: "200px" }),
    column<Row>({
      header: "Task",
      costCode: emptyCell,
      celi: (li) => {
        const { originalTask, proposedTask } = li;
        return proposedTask?.name ?? originalTask?.name ?? emptyCellDash;
      },
      w: 2,
    }),
  ];
}

const typeColor: Record<ChangeEventLineItemType, Properties> = {
  [ChangeEventLineItemType.Add]: Css.green500.$,
  [ChangeEventLineItemType.Remove]: Css.red600.$,
  [ChangeEventLineItemType.Modify]: Css.orange500.$,
  [ChangeEventLineItemType.Cutoff]: Css.gray700.$,
};

const typeOrder: Record<ChangeEventLineItemType, number> = {
  [ChangeEventLineItemType.Add]: 0,
  [ChangeEventLineItemType.Remove]: 1,
  [ChangeEventLineItemType.Modify]: 2,
  [ChangeEventLineItemType.Cutoff]: 3,
};

/** Orders a cost code's line items by type, adds first. */
function sortCelis<T extends { typeDetail: { code: string } }>(celis: T[]): T[] {
  return celis.sortBy((celi) => typeOrder[celi.typeDetail.code as ChangeEventLineItemType] ?? Number.MAX_SAFE_INTEGER);
}

/**
 * Lines on a plan-based bid only cover project items, the bid's own lines carry the cost and price. A line with
 * neither a cost nor a price is treated the same, e.g. a plan-based swap's added line that has no bid line yet.
 */
function isPlanBasedScopeOnly(celi: ScopeChangesTable_ChangeEventLineItemFragment): boolean {
  const onPlanBasedBid = [celi.originalBidContractLineItem, celi.proposedBidContractLineItem].some(
    (bcli) => bcli?.forCoveringTakeoffs,
  );
  return onPlanBasedBid || (celi.totalCostInCents === 0 && celi.totalPriceInCents === 0);
}

function isPriceOnly(celi: ScopeChangesTable_ChangeEventLineItemFragment): boolean {
  return celi.totalCostInCents === 0 && celi.totalPriceInCents !== 0;
}
